<script setup lang="ts">
import Accordion from "~/components/Common/Accordion.vue";
import AreaInput from "~/components/Common/Inputs/AreaInput.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import { RemoveBookingDtoRequestActionEnum } from "@simbelapp/order-sdk";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const props = withDefaults(
  defineProps<{
    linkedAttendeesCount?: number;
  }>(),
  {
    linkedAttendeesCount: 0,
  },
);

const emit = defineEmits<{
  close: [];
  back: [];
  confirm: [action: RemoveBookingDtoRequestActionEnum, comment?: string];
}>();

const linkedAttendeesText = computed(() => {
  if (props.linkedAttendeesCount === 1) {
    return "L'inscription d'un collaborateur est liée à une demande de formation.";
  }
  if (props.linkedAttendeesCount > 1) {
    return `L'inscription de ${props.linkedAttendeesCount} collaborateurs est liée à une demande de formation.`;
  }
  // Default: show "at least one" when count is 0 or unknown
  return "L'inscription d'au moins un collaborateur est liée à une demande de formation.";
});

const selectedAction = ref<RemoveBookingDtoRequestActionEnum>(RemoveBookingDtoRequestActionEnum.RollbackToPending);
const refuseComment = ref("");
const errorChoice = ref(false);
// Never released: the parent runs the removal with this modal still open, then unmounts it.
const isSubmitting = ref(false);

const isDefaultSelected = computed({
  get() {
    return selectedAction.value === RemoveBookingDtoRequestActionEnum.RollbackToPending;
  },
  set(value) {
    if (value) {
      selectedAction.value = RemoveBookingDtoRequestActionEnum.RollbackToPending;
      errorChoice.value = false;
    }
  },
});

const isRefuseSelected = computed({
  get() {
    return selectedAction.value === RemoveBookingDtoRequestActionEnum.Refuse;
  },
  set(value) {
    if (value) {
      selectedAction.value = RemoveBookingDtoRequestActionEnum.Refuse;
      errorChoice.value = false;
    }
  },
});

const isCancelSelected = computed({
  get() {
    return selectedAction.value === RemoveBookingDtoRequestActionEnum.Cancel;
  },
  set(value) {
    if (value) {
      selectedAction.value = RemoveBookingDtoRequestActionEnum.Cancel;
      errorChoice.value = false;
    }
  },
});

function hideModal() {
  emit("close");
}

function goBack() {
  emit("back");
}

function selectAction(action: RemoveBookingDtoRequestActionEnum) {
  selectedAction.value = action;
  errorChoice.value = false;
}

function confirmAction() {
  if (isSubmitting.value) return;
  if (!selectedAction.value) {
    errorChoice.value = true;
    return;
  }

  const comment = selectedAction.value === RemoveBookingDtoRequestActionEnum.Refuse ? refuseComment.value : undefined;

  isSubmitting.value = true;
  emit("confirm", selectedAction.value, comment);
}
</script>

<template>
  <Transition appear>
    <CenterModal style="width: 488px; height: auto" @hide="hideModal">
      <template #body>
        <div class="grid body_regular w-full p-[24px] gap-[24px]">
          <div class="flex flex-col gap-2">
            <h4 class="heading_4_demi">Que voulez-vous faire avec la demande liée ?</h4>
            <p class="text-secondary-color">
              {{ linkedAttendeesText }}
            </p>
          </div>

          <div class="flex flex-col gap-4">
            <div class="card-clickable" @click="selectAction(RemoveBookingDtoRequestActionEnum.RollbackToPending)">
              <Accordion v-model="isDefaultSelected" is-radio class="w-full pointer-events-none">
                <template #headers>
                  <div class="flex flex-col w-full">
                    <SvgIcon name="refresh" class="h-[24px] w-[24px] text-secondary-color"></SvgIcon>
                    <span>Réouvrir la demande</span>
                  </div>
                </template>
                <template #body>
                  <div class="flex flex-col gap-4 text-secondary-color">
                    <span class="body_regular text-secondary-color"> La demande repassera au statut "À traiter". </span>
                  </div>
                </template>
              </Accordion>
            </div>

            <div class="card-clickable" @click="selectAction(RemoveBookingDtoRequestActionEnum.Refuse)">
              <Accordion
                v-model="isRefuseSelected"
                is-radio
                class="w-full"
                :class="{ 'pointer-events-none': !isRefuseSelected }"
              >
                <template #headers>
                  <div class="flex flex-col w-full">
                    <SvgIcon name="cancel" class="h-[24px] w-[24px] text-secondary-color"></SvgIcon>
                    <span>Refuser la demande</span>
                  </div>
                </template>
                <template #body>
                  <div class="flex flex-col gap-2 pointer-events-auto">
                    <span class="body_regular text-secondary-color">Motif</span>
                    <AreaInput
                      v-model="refuseComment"
                      class="textarea__input"
                      style="min-height: 68px; max-height: 96px"
                      placeholder="Ajouter un commentaire"
                    />
                  </div>
                </template>
              </Accordion>
            </div>

            <div class="card-clickable" @click="selectAction(RemoveBookingDtoRequestActionEnum.Cancel)">
              <Accordion v-model="isCancelSelected" is-radio class="w-full pointer-events-none">
                <template #headers>
                  <div class="flex flex-col w-full">
                    <SvgIcon name="close" class="h-[24px] w-[24px] text-secondary-color"></SvgIcon>
                    <span>Annuler la demande</span>
                  </div>
                </template>
                <template #body>
                  <div class="flex flex-col gap-2 text-secondary-color">
                    <span class="body_regular text-secondary-color">
                      Une demande annulée reste visible par le manager et l'administrateur avec un statut "Annulée".
                    </span>
                    <span class="body_regular text-secondary-color">
                      Le collaborateur n'est pas notifié de cette annulation.
                    </span>
                  </div>
                </template>
              </Accordion>
            </div>

            <span v-if="errorChoice" class="text-primary-red-color body_regular">
              Vous devez sélectionner une option
            </span>
          </div>

          <div class="flex justify-center gap-[16px]">
            <AppButton type="outlined" label="Retour" class="w-full" :disabled="isSubmitting" @click="goBack" />
            <AppButton label="Continuer" class="w-full" :loading="isSubmitting" @click="confirmAction" />
          </div>
        </div>
      </template>
    </CenterModal>
  </Transition>
</template>

<style scoped lang="scss">
.card-clickable {
  cursor: pointer;
}

.v-enter-active {
  transition: opacity 0.3s;
}

.v-leave-active {
  transition: opacity 0;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
